import { useState, useEffect, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Box,
    CircularProgress,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf } from '@fortawesome/free-solid-svg-icons'
import { executionReportsRoutes } from '../routes'
import { dateWithTime } from '../config'
import ImageAstDialog from './ImageAstDialog'

const ReportItemsPreviewDialog = ({ open, handleClose, reportId, type, report, onGenerate, isGenerating, executionReport, loading }) => {
    const [error, setError] = useState(null)

    // Image viewer modal state
    const [openImageModal, setOpenImageModal] = useState(false)
    const [indexImage, setIndexImage] = useState(0)
    const [imagesSelected, setImagesSelected] = useState([])

    useEffect(() => {
        if (open) {
            if (!loading && !executionReport) {
                setError('No se encontró información para este reporte.')
            } else {
                setError(null)
            }
        }
    }, [open, executionReport, loading])

    const filteredItems = useMemo(() => {
        if (!executionReport || !executionReport.group) return []
        const items = []
        Object.keys(executionReport.group).forEach((sectionName) => {
            const sectionItems = executionReport.group[sectionName]
            if (Array.isArray(sectionItems)) {
                sectionItems.forEach((item) => {
                    if (type === 'no-checks') {
                        if (item.isChecked === true && item.isWarning === true) {
                            items.push({ ...item, sectionName })
                        }
                    } else if (type === 'findings') {
                        if (item.backlogList && item.backlogList.length > 0) {
                            items.push({ ...item, sectionName })
                        }
                    }
                })
            }
        })
        return items
    }, [executionReport, type])

    const openImage = (images, index) => {
        setOpenImageModal(true)
        setImagesSelected(images)
        setIndexImage(index)
    }

    const closeImage = () => {
        setOpenImageModal(false)
        setImagesSelected([])
        setIndexImage(0)
    }

    const titleText = type === 'no-checks'
        ? `Vista Previa - Tareas No Ejecutadas (OT ${report?.newIdIndex || report?.idIndex || ''})`
        : `Vista Previa - Resumen de Hallazgos (OT ${report?.newIdIndex || report?.idIndex || ''})`

    const buttonColor = type === 'no-checks' ? '#E67E22' : '#F2994A'

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            sx={{
                '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                        width: '100%',
                        maxWidth: '900px',
                        height: '100%',
                        maxHeight: '750px'
                    }
                }
            }}
        >
            {openImageModal && (
                <ImageAstDialog
                    open={openImageModal}
                    handleClose={closeImage}
                    images={imagesSelected}
                    indexSlide={indexImage}
                />
            )}
            <DialogTitle style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', padding: '16px 24px' }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>{titleText}</Typography>
                <IconButton onClick={handleClose} style={{ position: 'absolute', right: 8, top: 8 }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent style={{ padding: '24px', overflowY: 'auto' }}>
                {loading ? (
                    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '15px' }}>
                        <CircularProgress />
                        <Typography variant="body2" color="textSecondary">Cargando información previa...</Typography>
                    </Box>
                ) : error ? (
                    <Box style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Typography variant="h6" color="error">{error}</Typography>
                    </Box>
                ) : filteredItems.length === 0 ? (
                    <Box style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Typography variant="h6" color="textSecondary">
                            {type === 'no-checks'
                                ? 'No se registran tareas marcadas como no ejecutadas.'
                                : 'No se registran hallazgos en este reporte.'}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={1} style={{ borderRadius: '8px' }}>
                        <Table size="small">
                            <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 'bold', width: '25%' }}>Sección</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', width: '35%' }}>Tarea</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', width: '40%' }}>{type === 'no-checks' ? 'Comentario' : 'Hallazgo'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredItems.map((item, idx) => {
                                    if (type === 'no-checks') {
                                        const firstMsg = item.messages?.[0]
                                        const name = firstMsg?.name || 'N/A'
                                        const content = firstMsg?.content || 'Marcada como no ejecutada'
                                        const dateText = firstMsg?.id ? dateWithTime(firstMsg.id) : 'N/A'

                                        // Collect all images in messages
                                        const msgImages = (item.messages || [])
                                            .filter(m => m.urlBase64 || m.urlImageMessage)
                                            .map(m => m.urlBase64 || m.urlImageMessage)

                                        return (
                                            <TableRow key={idx}>
                                                <TableCell style={{ verticalAlign: 'top', fontSize: '12px', fontWeight: 'bold' }}>{item.sectionName}</TableCell>
                                                <TableCell style={{ verticalAlign: 'top', fontSize: '12px' }}>{item.taskdesc}</TableCell>
                                                <TableCell style={{ verticalAlign: 'top', fontSize: '11px' }}>
                                                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                                        <Box style={{ flex: 1 }}>
                                                            <Typography variant="caption" style={{ fontWeight: 'bold', color: '#E67E22' }}>
                                                                {name}
                                                            </Typography>
                                                            <Typography variant="body2" style={{ fontSize: '12px', marginTop: '2px', whiteSpace: 'pre-line' }}>
                                                                {content}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                                                                Fecha: {dateText}
                                                            </Typography>
                                                        </Box>
                                                        {msgImages.length > 0 && (
                                                            <Box style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', maxWidth: '170px', justifyContent: 'flex-end' }}>
                                                                {msgImages.map((imgSrc, imgIdx) => (
                                                                    <img
                                                                        key={imgIdx}
                                                                        src={imgSrc}
                                                                        alt="Evidencia"
                                                                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: '1px solid #ccc' }}
                                                                        onClick={() => openImage(msgImages, imgIdx)}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    } else {
                                        // Findings (backlogList)
                                        return (
                                            <TableRow key={idx}>
                                                <TableCell style={{ verticalAlign: 'top', fontSize: '12px', fontWeight: 'bold' }}>{item.sectionName}</TableCell>
                                                <TableCell style={{ verticalAlign: 'top', fontSize: '12px' }}>{item.taskdesc}</TableCell>
                                                <TableCell style={{ verticalAlign: 'top' }}>
                                                    {item.backlogList.map((backlog, bIdx) => {
                                                        const backlogUser = backlog.user
                                                            ? `${backlog.user.name} ${backlog.user.lastName}`
                                                            : 'Usuario Anónimo'
                                                        const backlogDate = backlog.id ? dateWithTime(backlog.id) : 'N/A'
                                                        const backlogImages = (backlog.images || backlog.fileList || [])
                                                            .map(img => (typeof img === 'string') ? img : img?.urlBase64 || img?.url)
                                                            .filter(Boolean)

                                                        return (
                                                            <Box key={bIdx} style={{ marginBottom: bIdx < item.backlogList.length - 1 ? '10px' : 0, paddingBottom: bIdx < item.backlogList.length - 1 ? '8px' : 0, borderBottom: bIdx < item.backlogList.length - 1 ? '1px dashed #eee' : 'none' }}>
                                                                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                                                    <Box style={{ flex: 1 }}>
                                                                        <Typography variant="caption" style={{ fontWeight: 'bold', color: '#be2e26' }}>
                                                                            {backlogUser}
                                                                        </Typography>
                                                                        <Typography variant="body2" style={{ fontSize: '12px', marginTop: '2px', whiteSpace: 'pre-line' }}>
                                                                            {backlog.message}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                                                                            Fecha: {backlogDate}
                                                                        </Typography>
                                                                    </Box>
                                                                    {backlogImages.length > 0 && (
                                                                        <Box style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', maxWidth: '170px', justifyContent: 'flex-end' }}>
                                                                            {backlogImages.map((imgSrc, imgIdx) => (
                                                                                <img
                                                                                    key={imgIdx}
                                                                                    src={imgSrc}
                                                                                    alt="Imagen"
                                                                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: '1px solid #ccc' }}
                                                                                    onClick={() => openImage(backlogImages, imgIdx)}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )
                                                    })}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #ccc' }}>
                <Button onClick={handleClose} variant="outlined" color="secondary">
                    Cerrar
                </Button>
                {filteredItems.length > 0 && (
                    <Button
                        onClick={onGenerate}
                        variant="contained"
                        disabled={isGenerating}
                        style={{
                            backgroundColor: isGenerating ? '#ccc' : buttonColor,
                            color: '#fff',
                            fontWeight: 'bold',
                            padding: '6px 20px'
                        }}
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={faFilePdf} />}
                    >
                        {isGenerating ? 'Generando PDF...' : 'Generar PDF'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ReportItemsPreviewDialog
